import { useField } from '@kizi/comparator-core';
import { Div, P } from '@kizi/core-components';
import { IconV2, withTooltip } from '@kizi/shared-components';
import * as React from 'react';

const TooltipIcon = withTooltip(IconV2);

export type GroupToShow = {
  label: JSX.Element;
  value: string | number | JSX.Element | undefined;
  icon: string | undefined;
};

export const PackageInformation = ({ data }) => {
  const filters = useField('filters');
  const filtersData = JSON.parse(filters?.value as string).productTypes;

  const RowItem = ({ label, value = '', icon = '', tooltip = '' }) => (
    <Div
      display="flex"
      justifyContent="space-between"
      mb={1}
      borderBottom="1px solid"
      borderBottomColor="second.1"
      py={1}
    >
      <P variant="bodyImportant">{label}</P>
      <Div display="flex" gridGap={1} alignItems="center">
        {value && <P variant="bodyRegular">{value}</P>}
        {icon && <IconV2 name={icon} width={28} height={28} />}
        {tooltip && (
          <TooltipIcon
            name="info"
            size={20}
            css={{ fill: 'second.0' }}
            _tooltip={tooltip}
            cursor="pointer"
          />
        )}
      </Div>
    </Div>
  );

  return (
    <Div display="flex" flexDirection="column" gridGap={4}>
      <Div
        display="flex"
        flexDirection={['column', 'column', 'row']}
        width={'100%'}
        gridRowGap={6}
      >
        <Div width={['100%', '100%', '50%']} pr={[0, 0, 7]} pt={[2, 0, 0]}>
          <P variant="bodyImportant" fontSize="20px!important" mb={2}>
            {'Contract'}
          </P>
          {data?.durationInMonths && (
            <RowItem
              label="Looptijd"
              value={`${data.durationInMonths} ${data.durationInMonths === '1' ? 'maand' : 'maanden'}`}
              tooltip="De minimale looptijd van dit contract."
            />
          )}
          {data?.averageDeliveryTimeInDays && (
            <RowItem
              label="Gemiddelde levertijd"
              value={`${data.averageDeliveryTimeInDays} dagen`}
              tooltip="De gemiddelde levertijd vanaf de aanvraag tot de levering."
            />
          )}
          <RowItem
            label="Overstapservice"
            icon={data?.switchingService ? 'green-check' : 'small-cancel'}
            tooltip="De nieuwe aanbieder regelt de opzegging met de oude aanbieder."
          />
          <RowItem
            label="Installatie opties"
            icon={data?.installationOptions ? 'green-check' : 'small-cancel'}
            tooltip="De mogelijkheid tot een monteur."
          />
        </Div>
        <Div width={['100%', '100%', '50%']}>
          <P variant="bodyImportant" fontSize="20px!important" mb={2}>
            {'Internet'}
          </P>
          {data?.downloadSpeedInMb && (
            <RowItem
              label="Downloadsnelheid"
              value={`${data.downloadSpeedInMb} Mbit/s`}
              tooltip="De maximale downloadsnelheid die haalbaar is met dit pakket."
            />
          )}
          {data?.uploadSpeedInMb && (
            <RowItem
              label="Uploadsnelheid"
              value={`${data.uploadSpeedInMb} Mbit/s`}
              tooltip="De maximale uploadsnelheid die haalbaar is met dit pakket."
            />
          )}
          {data?.network && (
            <RowItem
              label="Netwerk"
              value={data.network}
              tooltip="Het netwerk waarover de aanbieder de verbinding levert."
            />
          )}
        </Div>
      </Div>
      <Div
        display="flex"
        flexDirection={['column', 'column', 'row']}
        width={'100%'}
        gridRowGap={6}
      >
        {filtersData?.includes('television') && (
          <Div width={['100%', '100%', '50%']} pr={[0, 0, 7]} pt={[2, 0, 0]}>
            <P variant="bodyImportant" fontSize="20px!important" mb={2}>
              {'Televisie'}
            </P>
            {data?.tvChannels && (
              <RowItem
                label="TV zenders"
                value={data.tvChannels}
                tooltip="Het aantal tv-zenders bij dit product."
              />
            )}
            {data?.hdChannels && (
              <RowItem
                label="HD zenders"
                value={data.hdChannels}
                tooltip="Het aantal hd-tv-zenders bij dit product."
              />
            )}
            <RowItem
              label="TV via app"
              icon={data?.tvViaApp ? 'green-check' : 'small-cancel'}
              tooltip="De mogelijkheid dat TV beschikbaar is via een app."
            />
            <RowItem
              label="Terugkijken en pauzeren"
              icon={data?.reviewAndPause ? 'green-check' : 'small-cancel'}
              tooltip="De mogelijkheid om terug te kijken en te pauzeren."
            />
          </Div>
        )}
        {filtersData?.includes('call') && (
          <Div width={['100%', '100%', '50%']}>
            <P variant="bodyImportant" fontSize="20px!important" mb={2}>
              {'Bellen'}
            </P>
            {data?.numberPorting && (
              <RowItem
                label="Nummerbehoud"
                icon={data.numberPorting ? 'green-check' : 'small-cancel'}
                tooltip="De mogelijkheid om je huidige telefoonnummer mee te nemen naar de nieuwe aanbieder."
              />
            )}
            {data?.unlimitedCallingInNetherlands && (
              <RowItem
                label="Onbeperkt bellen NL"
                icon={
                  data.unlimitedCallingInNetherlands === 'true'
                    ? 'green-check'
                    : 'small-cancel'
                }
                tooltip="De mogelijkheid om onbeperkt te bellen in Nederland tegen betaling."
              />
            )}
            {data?.unlimitedCallingInEurope && (
              <RowItem
                label="Onbeperkt bellen EU"
                icon={
                  data.unlimitedCallingInEurope === 'true'
                    ? 'green-check'
                    : 'small-cancel'
                }
                tooltip="De mogelijkheid om onbeperkt te bellen in Europa tegen betaling."
              />
            )}
          </Div>
        )}
      </Div>
    </Div>
  );
};
